import type { IconName } from 'lucide-react/dynamic'
import dynamicIconImports from 'lucide-react/dynamicIconImports'
import { useEffect, useState } from 'react'

type IconNode = Awaited<ReturnType<(typeof dynamicIconImports)[IconName]>>['__iconNode']

const cache = new Map<IconName, IconNode>()

/**
 * The drawing instructions for a Lucide icon, loaded on demand.
 *
 * `DynamicIcon` renders null until its own import resolves and keeps that state to itself, which is
 * no use to a caller that has to wait for the icon before it can do anything with the rendered DOM.
 * This hands the node to the caller instead, so it can gate on it.
 *
 * A resolved icon is served from the cache during render, so only the first use of a name is async.
 */
export function useLucideIconNode(name: IconName): IconNode | null {
	const [, forceRender] = useState(0)
	const cached = cache.get(name)

	useEffect(() => {
		if (cache.has(name)) {
			return
		}
		// Icon names reach this from stored definitions, so an unknown one must not throw here.
		const load = dynamicIconImports[name]
		if (!load) {
			return
		}
		let active = true
		load()
			.then(module => {
				cache.set(name, module.__iconNode)
				if (active) {
					forceRender(count => count + 1)
				}
			})
			.catch(() => {})
		return () => {
			active = false
		}
	}, [name])

	return cached ?? null
}
